import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import Lottie from 'react-lottie-player/dist/LottiePlayerLight';
import { useHistory } from 'react-router';
import { getIDFromUrl } from '@rossum/api-client';
import { Queue } from '@rossum/api-client/queues';
import { Button, Stack, Typography } from '@rossum/ui/material';
import { GridFilterItem } from '@rossum/ui/x-data-grid-pro';
import { UPLOAD_HASH } from '../../../constants/values';
import { button, linebreak } from '../../../lib/formaterValues';
import { SuggestInbox } from '../../document-upload/components/SuggestInbox';
import { useGetInbox } from '../../document-upload/hooks/useGetInbox';
import * as uploadAnimation from './animations/upload.json';

type ClearFiltersButtonProps = {
  onClick: () => void;
};

const ClearFiltersButton = ({ onClick }: ClearFiltersButtonProps) => {
  const intl = useIntl();

  return (
    <Button
      color="secondary"
      variant="outlined"
      data-cy="clear-filters-btn"
      sx={{ mt: 3 }}
      onClick={onClick}
    >
      {intl.formatMessage({
        id: 'containers.allDocuments.emptyState.noResultsForFiltering.button.label',
      })}
    </Button>
  );
};

type EmptyStateTextsProps = {
  title: string;
  text: string | ReactNode;
};

const EmptyStateTexts = ({ title, text }: EmptyStateTextsProps) => {
  return (
    <>
      <Typography variant="h5" sx={{ color: 'text.secondary', mb: 2 }}>
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: 'text.secondary', textAlign: 'center' }}
      >
        {text}
      </Typography>
    </>
  );
};

type EmptyStatesProps = {
  searchQuery: string | undefined;
  isEmailView: boolean;
  shouldCreateQueue?: boolean;
  openQueueModal?: () => void;
  queue?: Queue | null;
  onErrorReset?: () => void;
  existingFilterItems: GridFilterItem[];
  onClearFilters?: () => void;
  uploadEnabled?: boolean;
};

export const EmptyStates = ({
  isEmailView,
  shouldCreateQueue,
  openQueueModal,
  queue,
  searchQuery,
  onErrorReset,
  existingFilterItems,
  onClearFilters,
  uploadEnabled = true,
}: EmptyStatesProps) => {
  const intl = useIntl();
  const history = useHistory();

  const showUploadDialog = () => {
    history.replace({ ...history.location, hash: UPLOAD_HASH });
  };

  const { data: queueInbox, isInitialLoading: queueInboxIsLoading } =
    useGetInbox(getIDFromUrl(queue?.inbox || ''));

  const shouldDisplayNoResultsForFiltering = queue
    ? Boolean(existingFilterItems.some(i => i.field !== 'queue'))
    : Boolean(existingFilterItems.length);

  const getContent = () => {
    if (shouldCreateQueue) {
      return (
        <>
          <Typography variant="h5" sx={{ color: 'text.secondary' }}>
            {intl.formatMessage({
              id: 'containers.allDocuments.emptyState.noQueues.title',
            })}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', my: 2 }}>
            {intl.formatMessage({
              id: 'containers.allDocuments.emptyState.noQueues.text',
            })}
          </Typography>
          {openQueueModal && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={openQueueModal}
            >
              {intl.formatMessage({
                id: 'containers.allDocuments.emptyState.noQueues.button',
              })}
            </Button>
          )}
        </>
      );
    }

    if (isEmailView) {
      if (shouldDisplayNoResultsForFiltering) {
        return (
          <>
            <EmptyStateTexts
              title={intl.formatMessage({
                id: 'containers.allDocuments.emptyState.noResultsForFiltering.title',
              })}
              text={intl.formatMessage({
                id: 'containers.allDocuments.emptyState.noResultsForFiltering.text',
              })}
            />
            {onClearFilters && <ClearFiltersButton onClick={onClearFilters} />}
          </>
        );
      }

      if (searchQuery) {
        return (
          <EmptyStateTexts
            title={intl.formatMessage(
              {
                id: `containers.allDocuments.emptyState.noResultsEmails.title`,
              },
              { search: searchQuery }
            )}
            text={intl.formatMessage({
              id: `containers.allDocuments.emptyState.noResultsEmails.text`,
            })}
          />
        );
      }

      return (
        <Stack alignItems="center" spacing={2}>
          <EmptyStateTexts
            title={intl.formatMessage({
              id: `containers.allDocuments.emptyState.noEmails.title`,
            })}
            text={intl.formatMessage({
              id: `containers.allDocuments.emptyState.noEmails.text`,
            })}
          />
          <SuggestInbox
            isLoading={queueInboxIsLoading}
            inboxEmail={queueInbox?.email}
            queueId={queue?.id}
          />
        </Stack>
      );
    }

    if (shouldDisplayNoResultsForFiltering) {
      return (
        <>
          <EmptyStateTexts
            title={intl.formatMessage({
              id: 'containers.allDocuments.emptyState.noResultsForFiltering.title',
            })}
            text={intl.formatMessage({
              id: 'containers.allDocuments.emptyState.noResultsForFiltering.text',
            })}
          />
          {onClearFilters && <ClearFiltersButton onClick={onClearFilters} />}
        </>
      );
    }

    if (searchQuery) {
      return (
        <EmptyStateTexts
          title={intl.formatMessage(
            { id: `containers.allDocuments.emptyState.noResults.title` },
            { search: searchQuery }
          )}
          text={intl.formatMessage({
            id: `containers.allDocuments.emptyState.noResults.text`,
          })}
        />
      );
    }

    if (onErrorReset)
      return (
        <EmptyStateTexts
          title={intl.formatMessage({
            id: `containers.allDocuments.emptyState.error.title`,
          })}
          text={intl.formatMessage(
            {
              id: `containers.allDocuments.emptyState.error.text`,
            },
            {
              button: button(onErrorReset),
              linebreak,
            }
          )}
        />
      );

    return (
      <>
        <Lottie
          loop
          play
          onClick={uploadEnabled ? showUploadDialog : undefined}
          animationData={uploadAnimation}
          style={{
            width: '12vw',
            height: '12vw',
            cursor: 'pointer',
          }}
        />

        <EmptyStateTexts
          title={intl.formatMessage({
            id: `containers.allDocuments.emptyState.noDocuments.title`,
          })}
          text={
            uploadEnabled
              ? intl.formatMessage(
                  {
                    id: `containers.allDocuments.emptyState.noDocuments.text`,
                  },
                  {
                    button: button(showUploadDialog),
                  }
                )
              : intl.formatMessage({
                  id: `containers.settings.queues.uploadDisabled`,
                })
          }
        />
      </>
    );
  };

  return (
    <Stack height={1} sx={{ overflowY: 'auto' }}>
      <Stack m="auto" alignItems="center">
        {getContent()}
      </Stack>
    </Stack>
  );
};
